import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Program from "@/pages/Program";
import SessionDetail from "@/pages/SessionDetail";
import Speakers from "@/pages/Speakers";
import SpeakerDetail from "@/pages/SpeakerDetail";
import Announcements from "@/pages/Announcements";
import Networking from "@/pages/Networking";
import Messages from "@/pages/Messages";
import Info from "@/pages/Info";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import NetworkingAbout from "@/pages/NetworkingAbout";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Watch from "@/pages/Watch";
import Quiz from "@/pages/Quiz";
import Bingo from "@/pages/Bingo";
import Playlist from "@/pages/Playlist";
import Feedback from "@/pages/Feedback";
import { shouldRedirectToVirtual, performVirtualRedirect } from "@/lib/redirect";
import { VIRTUAL_PLATFORM } from "@/lib/virtual";

function RequireAuth({ children, admin = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading…</div>;
  if (!user) {
    // Preserve the original destination so the user lands back here post-login.
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (admin && user.role !== "admin") return <Navigate to="/program" replace />;
  return children;
}

// Minimal wrapper for Privacy / Terms when accessed without sign-in (no nav).
function PublicPage({ children }) {
  return <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">{children}</div>;
}

// Brief splash shown for the millisecond between deciding to redirect and the
// browser actually navigating. Keeps the flash from looking broken on slow
// connections.
function RedirectingNotice() {
  return (
    <div
      data-testid="virtual-redirect-notice"
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        backgroundColor: "#000000",
        color: "#FFFFFF",
        fontFamily: "system-ui, -apple-system, sans-serif",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: "12px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#43C4E3", marginBottom: "12px" }}>
        DFTB26 has moved
      </div>
      <div style={{ fontSize: "22px", fontWeight: 600, marginBottom: "8px", maxWidth: "420px" }}>
        Taking you to {VIRTUAL_PLATFORM.host}…
      </div>
      <div style={{ fontSize: "13px", color: "rgba(255,255,255,0.7)", maxWidth: "420px", lineHeight: 1.5 }}>
        On-demand replays of every DFTB26 talk are over there now. If you&apos;re not redirected automatically,{" "}
        <a href={VIRTUAL_PLATFORM.url} style={{ color: "#43C4E3", textDecoration: "underline" }}>tap here</a>.
      </div>
    </div>
  );
}

export default function App() {
  // Hard JS-level redirect to virtual.dftb26.com for non-admin paths. Runs
  // BEFORE the router so we don't waste time mounting the full app shell.
  // See /lib/redirect.js for bypass rules.
  if (shouldRedirectToVirtual()) {
    performVirtualRedirect();
    return <RedirectingNotice />;
  }

  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster theme="dark" position="top-center" richColors />
          <Routes>
            {/* Public auth pages */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Public legal pages (app-store reviewers need these without sign-in) */}
            <Route path="/privacy" element={<PublicPage><Privacy /></PublicPage>} />
            <Route path="/terms" element={<PublicPage><Terms /></PublicPage>} />
            {/* Everything else is behind the sign-in gate */}
            <Route element={<RequireAuth><Layout /></RequireAuth>}>
              <Route index element={<Navigate to="/program" replace />} />
              <Route path="/program" element={<Program />} />
              <Route path="/program/:id" element={<SessionDetail />} />
              <Route path="/speakers" element={<Speakers />} />
              <Route path="/speakers/:id" element={<SpeakerDetail />} />
              <Route path="/announcements" element={<Announcements />} />
              <Route path="/networking" element={<Networking />} />
              <Route path="/messages/:id" element={<Messages />} />
              <Route path="/info" element={<Info />} />
              <Route path="/info/networking" element={<NetworkingAbout />} />
              <Route path="/watch" element={<Watch />} />
              <Route path="/quiz" element={<Quiz />} />
              <Route path="/bingo" element={<Bingo />} />
              <Route path="/playlist" element={<Playlist />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/admin" element={<RequireAuth admin><Admin /></RequireAuth>} />
            </Route>
            <Route path="*" element={<Navigate to="/program" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
